// Gezondheid per klantplan, gelijk aan het ontwerp (design/build/proto-klanten-logic.js, health): voortgang tegenover
// verstreken tijd, actieve taken over deadline en de einddatum. Gebruikt door Klantprojecten, Tijdlijn en de
// klantkeuze bovenaan. Onderaan de gezondheid van het team uit Clockify: wie nu waaraan werkt en de bezetting.
import { fmtHours, loadTone, weekLoad, type Absence, type CapOptions, type CapPerson, type Capacity, type ClockifyLive, type TaskHours, type Timer } from "./api-clockify";
import { catOf } from "./board";
import { dayDate } from "./cleanup";
import { ddmm, diffDays } from "./planning";
import type { Klant, Person, PlanningData, Task } from "./types";

export type HealthStatus = "Einde vandaag" | "Einde verstreken" | "In gevaar" | "Aandacht" | "Geen planning" | "Afsluiten" | "Op schema";
export type HealthAction = "dates" | "close" | "shift" | "look";

export const HEALTH_RANK: Record<HealthStatus, number> = { "Einde vandaag": 0, "Einde verstreken": 1, "In gevaar": 2, "Aandacht": 3, "Geen planning": 4, "Afsluiten": 5, "Op schema": 6 };
/** pill-toon (ex-pill--err, --warn, --ok, --off) */
export const HEALTH_TONE: Record<HealthStatus, "err" | "warn" | "ok" | "off"> = { "Einde vandaag": "err", "Einde verstreken": "err", "In gevaar": "err", "Aandacht": "warn", "Geen planning": "off", "Afsluiten": "ok", "Op schema": "ok" };

export interface KlantHealth {
  klant: Klant;
  status: HealthStatus;
  tone: "err" | "warn" | "ok" | "off";
  why: string;
  action: HealthAction;
  /** % van de looptijd voorbij, % van de taken af */
  timePct: number;
  donePct: number;
  /** dagen tot het einde (negatief: voorbij) */
  left: number;
  open: number;
  actief: number;
  geparkeerd: number;
  afsluiten: number;
  /** actieve taken over deadline */
  over: number;
  rank: number;
}

const nOf = (x: number, one: string, many: string) => x + " " + (x === 1 ? one : many);

export function klantHealth(k: Klant, data: PlanningData, finish = k.finish): KlantHealth {
  const today = data.today;
  const open = data.tasks.filter((t) => t.projectId === k.id);
  const act = open.filter((t) => catOf(t) === "actief");
  const over = act.filter((t) => t.end < today).length;
  const allClose = open.length > 0 && open.every((t) => catOf(t) === "afsluiten");
  const donePct = k.total ? Math.round((k.done / k.total) * 100) : 0;
  const left = diffDays(today, finish), span = Math.max(1, diffDays(k.start, finish));
  const timePct = Math.max(0, Math.min(100, Math.round((diffDays(k.start, today) / span) * 100)));
  const late = nOf(over, "actieve taak is", "actieve taken zijn") + " over deadline.";
  let status: HealthStatus, why: string, action: HealthAction;
  if (k.start === finish) { status = "Geen planning"; why = "Het plan heeft geen looptijd: start en einde vallen op " + ddmm(k.start) + ". " + (open.length === 1 ? "Er staat 1 open taak in." : "Er staan " + open.length + " open taken in."); action = "dates"; }
  else if (left < 0 && allClose) { status = "Afsluiten"; why = "Het einde was " + ddmm(finish) + " en alle open taken dragen al Prod of Test validated."; action = "close"; }
  else if (left < 0) { status = "Einde verstreken"; why = "De einddatum " + ddmm(finish) + " is " + (-left) + " dagen voorbij. " + over + " van " + act.length + " actieve taken zijn over deadline."; action = "shift"; }
  else if (left === 0) { status = "Einde vandaag"; why = "De einddatum is vandaag en " + donePct + " % is klaar. " + late; action = "shift"; }
  else if (over > 0 && timePct - donePct >= 40) { status = "In gevaar"; why = donePct + " % is klaar terwijl " + timePct + " % van de tijd voorbij is. " + late; action = "look"; }
  else if (over > 0) { status = "Aandacht"; why = late.charAt(0).toUpperCase() + late.slice(1) + " " + donePct + " % is klaar bij " + timePct + " % van de tijd."; action = "look"; }
  else if (left <= 14 && act.length) { status = "Aandacht"; why = "Het einde valt over " + left + " dagen, op " + ddmm(finish) + ". Nog " + nOf(act.length, "actieve taak", "actieve taken") + ", geen enkele te laat."; action = "look"; }
  else { status = "Op schema"; why = "Geen actieve taken over deadline. " + donePct + " % is klaar, het einde valt op " + ddmm(finish) + "."; action = "look"; }
  return {
    klant: k, status, tone: HEALTH_TONE[status], why, action, timePct, donePct, left, open: open.length, actief: act.length,
    geparkeerd: open.filter((t) => catOf(t) === "geparkeerd").length, afsluiten: open.filter((t) => catOf(t) === "afsluiten").length,
    over, rank: HEALTH_RANK[status],
  };
}

/** Alle klanten, de grootste risico's eerst. */
export function allHealth(data: PlanningData): KlantHealth[] {
  return data.klanten.map((k) => klantHealth(k, data)).sort((a, b) => a.rank - b.rank || b.over - a.over || a.klant.name.localeCompare(b.klant.name));
}

// ---------- team: Clockify ----------
// Wie nu waaraan werkt (bovenbalk, Team nu) en de bezetting per week (Mijn werk, Klantprojecten). De
// bezetting telt zoals Planning · Capaciteit standaard: de achterstand verdeeld over vier weken, support als vaste
// reservering per week. Een timer die langer dan 12 u loopt, is wellicht vergeten en geldt niet als bezig.

export const CAP_DEFAULT: CapOptions = { backlogWeeks: 4, withSupport: true };

export interface Thresholds { room: number; full: number; over: number }
/** Drempels voor de kleur uit Instellingen (in %); zolang die laden de standaardwaarden. */
export const thresholds = (s?: Thresholds | null): Thresholds => (s ? { room: s.room, full: s.full, over: s.over } : { room: 50, full: 90, over: 100 });

const TONE_TXT = { afwezig: "t-mute", ruimte: "", normaal: "", vol: "t-warn", overvol: "t-err" } as const;
const TONE_BAR = { afwezig: "p-load", ruimte: "p-load low", normaal: "p-load ok", vol: "p-load warn", overvol: "p-load err" } as const;

export interface Bezetting {
  load: number;
  cap: number;
  /** null: in die week geen beschikbare uren */
  pct: number | null;
  tone: ReturnType<typeof loadTone>;
  /** "104 %", of de reden van de afwezigheid ("verlof") */
  text: string;
  /** klasse voor de tekst en voor de balk (p-load), en de breedte van de balk in % */
  txtCls: string;
  barCls: string;
  width: number;
}

/** Bezetting van een persoon in week i (0 = deze week): enkel afwijkingen krijgen kleur. */
export function bezetting(p: CapPerson, i: number, th: Thresholds): Bezetting {
  const w = weekLoad(p, i, CAP_DEFAULT), tone = loadTone(w.pct, th);
  const why = p.weeks[i]?.absent[0]?.kind;
  return { ...w, tone, text: w.pct == null ? why || "afwezig" : w.pct + " %", txtCls: TONE_TXT[tone], barCls: TONE_BAR[tone], width: Math.min(100, w.pct ?? 0) };
}

/** bezig: timer loopt; vergeten: timer loopt langer dan 12 u; afwezig: vandaag verlof, feestdag of sluiting;
 *  laatst: geen timer, wel in Clockify; niet: niet gevonden in Clockify (ander e-mailadres) */
export type TeamKind = "bezig" | "vergeten" | "afwezig" | "laatst" | "niet";
export interface TeamRow {
  p: Person;
  kind: TeamKind;
  timer: Timer | null;
  /** de afwezigheid van vandaag (uit de capaciteit; null zonder capaciteit) */
  absent: Absence | null;
  /** einde van de laatste registratie */
  last: string | null;
  cap: CapPerson | null;
}

/** Elke collega met wat die nu doet volgens Clockify. */
export function teamNow(data: PlanningData, live: ClockifyLive, cap?: Capacity | null): TeamRow[] {
  const linked = new Set(live.linked);
  const capOf = new Map((cap?.people || []).map((c) => [c.personId, c]));
  return data.people.map((p) => {
    const timer = live.timers.find((t) => t.personId === p.id) || null;
    const c = capOf.get(p.id) || null;
    const absent = c?.weeks[0]?.absent.find((a) => a.from <= data.today && a.to >= data.today) || null;
    const kind: TeamKind = timer && !timer.stale ? "bezig" : absent ? "afwezig" : timer ? "vergeten" : linked.has(p.id) ? "laatst" : "niet";
    return { p, kind, timer, absent, last: live.lastActive[p.id] || null, cap: c };
  });
}

/** Waar een timer op loopt: de Planner-taak (of de Clockify-taak), de omschrijving als die iets toevoegt, en de klant. */
export function timerText(t: Timer, task?: Task | null): string {
  const name = task?.title || t.taskName, low = (s: string) => s.trim().toLowerCase();
  const desc = t.description && (!name || low(t.description) !== low(name)) ? t.description : "";
  return [name, desc, t.client || t.project].filter(Boolean).join(" · ") || "zonder omschrijving";
}

/** "verlof t/m vrijdag 26/09", "Wapenstilstand vandaag" */
export function absentText(a: Absence, today: string): string {
  const what = a.kind === "verlof" ? "verlof" : a.label || (a.kind === "feestdag" ? "feestdag" : "sluitingsdag");
  return what + (a.to === today ? " vandaag" : " t/m " + dayDate(a.to));
}

/** Uren van een taak voor een rij: "4 / 24 u" (rood als er meer gelogd is), "geen schatting", of "–" bewust zonder
 *  Clockify-taak. cls is de klasse voor de span met het klokje. */
export function hoursText(h: TaskHours | undefined): { text: string; cls: string; title: string } {
  if (h?.none) return { text: "–", cls: "p-meta-i t-mute", title: "Bewust zonder Clockify-taak" };
  const est = h?.estimate || null, logged = h?.logged || 0;
  if (est == null) {
    const why = logged ? fmtHours(logged) + " gelogd, nog geen schatting" : h ? "Nog geen schatting in Clockify" : "Nog niet gekoppeld aan een Clockify-taak";
    return { text: "geen schatting", cls: "p-meta-i t-warn", title: why };
  }
  return { text: fmtHours(logged).replace(" u", "") + " / " + fmtHours(est), cls: "p-meta-i" + (logged > est ? " t-err" : ""), title: fmtHours(logged) + " gelogd van " + fmtHours(est) + " geschat" };
}

/** "Jens", "Jens en Arthur", "Jens, Lennert en Arthur" */
export const andList = (xs: string[]) => (xs.length > 1 ? xs.slice(0, -1).join(", ") + " en " + xs[xs.length - 1] : xs[0] || "");

/** "26/09", "12–16/10" (zelfde maand) of "28/09–02/10" */
export const dayRange = (a: string, b: string) => (a === b ? ddmm(a) : a.slice(0, 7) === b.slice(0, 7) ? Number(a.slice(8, 10)) + "–" + ddmm(b) : ddmm(a) + "–" + ddmm(b));
